import React, {useCallback, useMemo} from 'react';
import {useParams} from 'react-router';
import {Box, Button, CircularProgress, Stack, Typography} from '@mui/material';
import {addMetaMark, removeMetaMarkByUid, scrollToBlockId} from '../../../../../components/prosemirror-editor';
import {blockRangeToDocPositions} from '../../../../../components/prosemirror-editor/helpers/blockRangeToDocPositions';
import {applyStatementReferenceMark} from '../../../../../components/prosemirror-editor/helpers/applyStatementReferenceMark';
import {useReportStore} from '../../../useReportStore';
import {TextSelection} from 'prosemirror-state';
import {setReportTaskStatus} from '../../../../../firebase/firestore/reportTasks';
import ImprovementsTaskList from './ImprovementsTaskList';
import {buildWikidataEntityMarkAttrs} from '../../../../../components/prosemirror-editor/helpers/wikidataHelpers';
import {syncKbEntityFromWikidata} from '../../../../../utils/syncKbEntityFromWikidata';
import {filterApplicableSuggestions} from '../../../../../utils/suggestionApplicability';
import {useLiveEditorDoc} from '../../hooks/useLiveEditorDoc';

const AIAssistantTaskView = ({viewKey, viewConfig, allTasks, loading, error}) => {
    const {reportId} = useParams();
    const editorView = useReportStore(s => s.editorView);
    const liveDoc = useLiveEditorDoc(editorView);

    // Filter, deduplicate, and sort tasks by document position (top → bottom)
    const tasks = useMemo(() => {
        if (!viewConfig?.isTaskBased || !allTasks) return [];

        const filtered = filterApplicableSuggestions(
            allTasks.filter(t => t.taskType === viewKey),
            liveDoc,
        );

        // Deduplicate: for entity tasks, same blockId+range+entity identity is the same suggestion.
        // Keep the task with the lowest id (earliest write) when duplicates exist.
        const seen = new Set();
        const deduped = filtered.filter(t => {
            let key;
            if (t.taskType === 'entity_recognition') {
                const rangeKey = Array.isArray(t.range) ? t.range.join(',') : '';
                const entityKey = t.entity?.id || t.entity?.label || '';
                key = `${t.blockId}|${rangeKey}|${entityKey}`;
            } else {
                const rangeKey = Array.isArray(t.range) ? t.range.join(',') : '';
                key = `${t.blockId}|${rangeKey}|${t.taskType}`;
            }
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

        return deduped.sort((a, b) => {
            const posA = typeof a.position === 'number' ? a.position : Infinity;
            const posB = typeof b.position === 'number' ? b.position : Infinity;
            if (posA !== posB) return posA - posB;
            const rangeA = Array.isArray(a.range) ? a.range[0] : 0;
            const rangeB = Array.isArray(b.range) ? b.range[0] : 0;
            return rangeA - rangeB;
        });
    }, [allTasks, viewKey, viewConfig?.isTaskBased, liveDoc]);

    const canNavigateTask = useCallback((task) => !!(task?.blockId && editorView), [editorView]);

    const handleTaskClick = useCallback((task) => {
        if (task?.blockId && editorView) {
            const range = Array.isArray(task?.range) && task.range.length === 2 ? task.range : undefined;
            scrollToBlockId(editorView, task.blockId, range ? {range} : undefined);
        }
    }, [editorView]);

    const handleAccept = useCallback(async (task) => {
        if (!reportId || !task?.id) return;

        // For reference_analysis tasks, "Accept" means apply a reference mark
        if (task.taskType === 'reference_analysis') {
            if (!editorView) return;

            if (task?.blockId) {
                scrollToBlockId(editorView, task.blockId);
            }

            const result = applyStatementReferenceMark(editorView, {
                blockId: task?.blockId,
                range: task?.range,
                text: task?.text,
            });

            if (!result.ok) {
                console.warn('Accept statement task failed:', result.reason, task);
                return;
            }

            await setReportTaskStatus(reportId, task.id, 'accepted');
            return;
        }

        // For entity_recognition tasks, "Accept" means apply the entity mark
        if (task.taskType === 'entity_recognition' && task.entity) {
            // Apply wikidata entity mark
            if (!editorView || !task?.blockId || !Array.isArray(task?.range)) return;

            const [start, end] = task.range;
            scrollToBlockId(editorView, task.blockId);

            const docRange = blockRangeToDocPositions(editorView, task.blockId, start, end);
            if (!docRange) {
                console.warn('Accept entity task: could not map block range to doc positions', task);
                return;
            }

            const {state, dispatch} = editorView;
            const {schema} = state;
            const markType = schema.marks.wikidataEntity;

            if (!markType) {
                console.warn('wikidataEntity mark type not found in schema');
                return;
            }

            const entity = task.entity;
            const mark = markType.create(buildWikidataEntityMarkAttrs(entity));

            const tr = state.tr
                .addMark(docRange.from, docRange.to, mark)
                .removeStoredMark(markType); // Don't continue mark typing

            dispatch(tr);
            editorView.focus();

            syncKbEntityFromWikidata(entity);

            // Mark task as accepted
            await setReportTaskStatus(reportId, task.id, 'accepted');
            return;
        }

        // For add_tags tasks, "Accept" means apply the tag mark
        if (task.taskType === 'add_tags' && task.tags) {
            if (!editorView || !task?.blockId || !Array.isArray(task?.range)) return;

            const [start, end] = task.range;
            scrollToBlockId(editorView, task.blockId);

            const docRange = blockRangeToDocPositions(editorView, task.blockId, start, end);
            if (!docRange) {
                console.warn('Accept add_tags task: could not map block range to doc positions', task);
                return;
            }

            const {state, dispatch} = editorView;
            const {schema} = state;
            const markType = schema.marks.tag;

            if (!markType) {
                console.warn('tag mark type not found in schema');
                return;
            }

            // Apply tag mark(s) - task.tags should be an array of tag strings
            const tags = Array.isArray(task.tags) ? task.tags : [task.tags];

            let tr = state.tr;
            tags.forEach(tagName => {
                const mark = markType.create({tag: tagName});
                tr = tr.addMark(docRange.from, docRange.to, mark);
            });

            tr = tr.removeStoredMark(markType); // Don't continue mark typing
            dispatch(tr);
            editorView.focus();

            // Mark task as accepted
            await setReportTaskStatus(reportId, task.id, 'accepted');
            return;
        }

        // For other tasks, apply task mark (original logic)
        // First persist status change.
        await setReportTaskStatus(reportId, task.id, 'accepted');

        // Then apply editor mark (best-effort).
        if (!editorView || !task?.blockId || !Array.isArray(task?.range)) return;

        const [start, end] = task.range;
        // Give the user a visual anchor even if applying the mark fails.
        scrollToBlockId(editorView, task.blockId);

        const docRange = blockRangeToDocPositions(editorView, task.blockId, start, end);
        if (!docRange) {
            console.warn('Accept task: could not map block range to doc positions', task);
            return;
        }

        const uid = task.id;

        const {state, dispatch} = editorView;
        const tr = state.tr.setSelection(TextSelection.create(state.doc, docRange.from, docRange.to));
        dispatch(tr);

        // Ensure we don't create duplicate marks with the same uid.
        removeMetaMarkByUid(editorView, 'task', uid);
        addMetaMark(editorView, 'task', uid);

        // Clear selection after applying the mark (leave a cursor at the end of the range).
        const nextState = editorView.state;
        const collapsePos = Math.min(docRange.to, nextState.doc.content.size);
        dispatch(nextState.tr.setSelection(TextSelection.create(nextState.doc, collapsePos)));

        editorView.focus();
    }, [editorView, reportId]);

    const handleReject = useCallback(async (task) => {
        if (!reportId || !task?.id) return;
        await setReportTaskStatus(reportId, task.id, 'rejected');
    }, [reportId]);

    const handleSelectAnotherEntity = useCallback((task) => {
        // For entity_recognition, open the entity search dialog for the range
        if (!editorView || !task?.blockId || !Array.isArray(task?.range)) return;

        const [start, end] = task.range;
        scrollToBlockId(editorView, task.blockId);

        const docRange = blockRangeToDocPositions(editorView, task.blockId, start, end);
        if (!docRange) {
            console.warn('Select another entity: could not map block range to doc positions', task);
            return;
        }

        const {state} = editorView;

        // Set selection to the task range so the entity search dialog can use it
        const tr = state.tr.setSelection(TextSelection.create(state.doc, docRange.from, docRange.to));
        editorView.dispatch(tr);
        editorView.focus();

        // Trigger the openEntitySearch event
        const event = new CustomEvent('openEntitySearch', {
            detail: {view: editorView}
        });
        window.dispatchEvent(event);
    }, [editorView]);

    // Non-task based views (alternative hypotheses, second opinion)
    if (!viewConfig?.isTaskBased) {
        return (
            <Box p={3}>
                <Stack spacing={2} alignItems="center" justifyContent="center" height="100%">
                    <Typography variant="body1" color="text.secondary" textAlign="center">
                        {viewKey === 'alternative_hypotheses'
                            ? 'Click the button below to generate alternative hypotheses for your analysis.'
                            : 'Click the button below to request a second opinion on your analysis.'}
                    </Typography>
                    <Button variant="contained" color="primary">
                        {viewKey === 'alternative_hypotheses'
                            ? 'Generate Hypotheses'
                            : 'Request Second Opinion'}
                    </Button>
                </Stack>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={3}>
                <CircularProgress/>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Typography color="error">
                    Error loading tasks: {error.message}
                </Typography>
            </Box>
        );
    }

    if (!tasks || tasks.length === 0) {
        const isComplianceView = viewKey === 'compliance_check' || viewKey === 'best_practices_check';
        const label = viewConfig?.label || (isComplianceView ? 'suggestions' : 'tasks');
        const storedCount = allTasks?.filter(t => t.taskType === viewKey).length || 0;
        return (
            <Box p={3}>
                <Typography color="text.secondary">
                    {isComplianceView && storedCount > 0
                        ? `No ${label} suggestions match the current draft.`
                        : isComplianceView
                        ? `No ${label} suggestions yet. Checks run automatically — results appear within a few seconds of opening or editing the report.`
                        : `No ${label} found`}
                </Typography>
            </Box>
        );
    }

    return (
        <ImprovementsTaskList
            tasks={tasks}
            editorView={editorView}
            canNavigateTask={canNavigateTask}
            onTaskNavigate={handleTaskClick}
            onTaskAccept={handleAccept}
            onTaskReject={handleReject}
            onTaskSelectAnotherEntity={handleSelectAnotherEntity}
        />
    );
};

export default AIAssistantTaskView;

